import React, { Component } from 'react'
import 'views/splash-screen/SplashScreen.scss'

const logo = `${process.env.PUBLIC_URL || ''}/logo/logo-white-256.png`

class ErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = {
      hasError: false,
      errorInfo: null,
      isCheckingVersion: false,
      hasNewVersion: false,
      latestVersion: null,
    }
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true }
  }

  componentDidCatch(error, errorInfo) {
    window.ckdStartup?.complete();
    // You can log the error or send it to an error reporting service
    console.error('Error caught by ErrorBoundary: ', error, errorInfo)
    this.setState({ errorInfo })
    this.checkVersion()
  }

  getSubpath = () => {
    const pathname = window.location.pathname
    const subpath = pathname.split('/')[1]
    return subpath ? `/${subpath}` : ''
  }

  checkVersion = async () => {
    this.setState({ isCheckingVersion: true })

    try {
      const subpath = this.getSubpath()
      const formattedSubpath = subpath.endsWith('/') ? subpath : `${subpath}/`
      const timestamp = Date.now()
      const response = await fetch(`${formattedSubpath}manifest.json?v=${timestamp}`, {
        cache: 'no-store',
      })
      const manifest = await response.json()
      const latestVersion = manifest?.version || null
      const currentVersion = localStorage.getItem('ckdoctor_build_version') || null

      if (latestVersion && latestVersion !== currentVersion) {
        this.setState({
          hasNewVersion: true,
          latestVersion,
        })
      }
    } catch (error) {
      console.error('Error checking app version:', error)
    } finally {
      this.setState({ isCheckingVersion: false })
    }
  }

  clearAppCache = () => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('ckd-cache-clear'))
    }

    const deleteIndexedDB = new Promise((resolve) => {
      if (!window.indexedDB) return resolve()

      const request = window.indexedDB.deleteDatabase('ckd-cache')
      request.onsuccess = resolve
      request.onerror = resolve
      request.onblocked = resolve
    })

    const deleteCacheStorage =
      window.caches && window.caches.keys
        ? window.caches
            .keys()
            .then((cacheNames) =>
              Promise.all(cacheNames.map((cacheName) => window.caches.delete(cacheName))),
            )
            .catch(() => {})
        : Promise.resolve()

    return Promise.all([deleteIndexedDB, deleteCacheStorage])
  }

  updateApp = async () => {
    if (this.state.latestVersion) {
      localStorage.setItem('ckdoctor_build_version', this.state.latestVersion)
    }

    await this.clearAppCache()

    if (navigator.serviceWorker) {
      const registration = await navigator.serviceWorker.getRegistration()
      if (registration?.waiting) {
        registration.waiting.postMessage({ type: 'SKIP_WAITING' })
      }
      if (navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({ type: 'SKIP_WAITING' })
      }
    }

    window.location.reload()
  }

  render() {
    if (this.state.hasError) {
      // You can render any custom fallback UI here
      const title = this.state.hasNewVersion
        ? 'New version available'
        : 'We’re refreshing things for you...'
      const message = this.state.hasNewVersion
        ? 'A newer CK-Doctor version is ready. Update the app to continue.'
        : 'Our system may be upgrading or experiencing a temporary issue. Please try again in a moment.'
      const buttonLabel = this.state.hasNewVersion ? 'Update Now' : 'Try Again'
      const onClick = this.state.hasNewVersion
        ? this.updateApp
        : () => window.location.reload()

      return (
        <div>
          <div className="splash-screen error-boundary">
            <div className='login-pf-page'>
              <div id="kc-header" className="login-pf-page-header">

                <div className="logo">
                  <img alt="CkDoctor" src={logo} width="256"></img>
                </div>
              </div>
              <div className="card-pf">
                <header className="login-pf-header">
                  <h1 id="kc-page-title">    CK-Doctor</h1>
                  <h1>{title}</h1>
                  <p>{message}</p>
                  {this.state.isCheckingVersion && (
                    <p>Checking latest version...</p>
                  )}
                  <div className="button-container">
                    <button className="button-primary" onClick={onClick}>
                      {buttonLabel}
                    </button>
                  </div>
                </header>
                <details style={{ whiteSpace: 'pre-wrap' }}>
                  {this.state.errorInfo && this.state.errorInfo.componentStack}
                </details>

              </div>
            </div>

            {/* Add any other loading indicators or branding */}
          </div>
        </div>
      )
    }

    // When there's no error, render children
    return this.props.children
  }
}

export default ErrorBoundary
